// POST /api/v1/admin/portfolios/:portfolio_id/reopen — lift a
// consent-withdrawal stall. Server restores 'composed' when a drop
// exists, else 'recruiting'.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type ReopenOutcome = {
  portfolio_id: string;
  recruiting_status: string;
};

async function reopenPortfolio(input: {
  portfolio_id: string;
}): Promise<ReopenOutcome> {
  const response = await fetch(
    `/api/v1/admin/portfolios/${input.portfolio_id}/reopen`,
    {
      method: "POST",
      credentials: "include",
      // /api/v1/admin/portfolios is a FINANCIAL_MUTATION_PREFIX —
      // POSTs without this header 400 (IDEMPOTENCY_KEY_REQUIRED).
      headers: { "x-idempotency-key": crypto.randomUUID() },
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Reopen failed (HTTP ${response.status})`;
    try {
      const body = (await response.json()) as {
        error?: { message?: string };
      };
      message = body.error?.message ?? message;
    } catch {
      // non-JSON body
    }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: ReopenOutcome };
  return body.data;
}

export function useReopenPortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reopenPortfolio,
    onSuccess: (outcome) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio", outcome.portfolio_id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["shop-drops"] });
    },
    onError: (error) => {
      toast.error((error as Error).message);
    },
  });
}
